import { Edit2 } from '@styled-icons/feather/Edit2';
import { Eye } from '@styled-icons/octicons';
import classNames from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSpaceAndPageIDFromRouter } from '../../../../../data/space/hooks';
import ButtonBlack from '../../../../Button/ButtonBlack';
import ButtonBoxBorder from '../../../../Button/ButtonBoxBorder';
import EditorTool from '../../../EditorTool';
import {
  CUSTOMIZE_TEMPLATE_REQUEST_VALUE,
  CUSTOMIZE_TEMPLATE_RETURN_VALUE,
  CUSTOMIZE_TEMPLATE_UPDATED_IFRAME,
} from '../../../constants';
import { CollectionSettingsPathContext } from '../../../context/CollectionSettingsContextProvider/pathContext';
import { useTEditorRef } from '../../../plate';
import { BLOCK_ELEMENT_KEY, ICollectionTemplate } from '../../../types';
import styles from '../CollectionSettings.module.css';
import Customize from '../Customize';
import Preview from '../Preview';
import { SETTINGS_PAGE } from '../constants';
import {
  CollectionSettingsDataContext,
  CollectionSettingsPageDispatchContext,
} from '../contextProvider';

function CustomizeTemplate() {
  const { collectionData, element, editorSyncData, grabElement } = useContext(
    CollectionSettingsDataContext
  );
  const editor = useTEditorRef();
  const [preview, setpreview] = useState(false);
  const [loading, setloading] = useState(false);
  const [template, settemplate] = useState(element?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE]);
  const setSettingsPage = useContext(CollectionSettingsPageDispatchContext);
  const path = useContext(CollectionSettingsPathContext);
  const goToTemplate = () => {
    setSettingsPage(SETTINGS_PAGE.TEMPLATE);
  };
  const [, pageID] = useSpaceAndPageIDFromRouter();
  const oriTemplateRef = useRef(element?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE]);

  const [currentOrigin, setCurrentOrigin] = useState('');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setCurrentOrigin(window?.location?.origin || '');
  }, []);

  useEffect(() => {
    oriTemplateRef.current = element?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE];
  }, [element?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE]]);

  const onChangeTemplate = (
    newTemplate: ICollectionTemplate['template'],
    newSchema: { [key: string]: string },
    newSchemaOrder: string[]
  ): ICollectionTemplate => {
    const oriTemplate: ICollectionTemplate = JSON.parse(JSON.stringify(oriTemplateRef.current));
    if (oriTemplate) {
      oriTemplate.template = newTemplate;
      oriTemplate.schema = newSchema;
      oriTemplate.schemaOrder = newSchemaOrder;
      oriTemplate.custom = true;
      settemplate(oriTemplate);
      return oriTemplate;
    }
    return null;
  };

  const getCurrentValueFromIframe = () =>
    new Promise((res, rej) => {
      const channel = new MessageChannel();

      function customizeTemplateMessage(event: MessageEvent): ICollectionTemplate {
        const data = event?.data;
        if (
          data?.pageID !== pageID ||
          data?.type !== CUSTOMIZE_TEMPLATE_RETURN_VALUE ||
          data?.elementID !== element?.id
        )
          return null;
        const dataTemplate = data?.template;
        const schema = data?.schema;
        const schemaOrder = data?.schemaOrder;
        return onChangeTemplate(dataTemplate, schema, schemaOrder);
      }

      channel.port1.onmessage = (event: MessageEvent) => {
        const data = event?.data;

        channel.port1.close();
        if (data.error) {
          rej(data.error);
        } else {
          res(customizeTemplateMessage(event));
        }
      };

      const iframe = document.getElementById(
        CUSTOMIZE_TEMPLATE_UPDATED_IFRAME
      ) as HTMLIFrameElement;

      iframe.contentWindow.postMessage(
        {
          type: CUSTOMIZE_TEMPLATE_REQUEST_VALUE,
        },
        currentOrigin,
        [channel.port2]
      );
    });

  const onUseTemplate = async () => {
    setloading(true);
    const iframeTemplate = await getCurrentValueFromIframe();

    if (iframeTemplate) {
      EditorTool.setBlockProperty(editor, BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE, iframeTemplate, {
        at: path,
      });
      grabElement(path);
      await editorSyncData(collectionData);
    }
    setloading(false);
  };

  return (
    <div
      className={classNames(
        styles.containerItem,
        styles.containerWithSidebar,
        'is-flex is-flex-direction-column pl-6 pt-6'
      )}
    >
      <div className="is-flex is-flex-direction-row is-align-items-flex-end is-justify-content-space-between">
        <div className="is-flex is-flex-direction-column">
          <div
            className={classNames(
              'has-fit-content-width with-cursor-pointer is-subtitle with-light-text-color mb-3'
            )}
            role="none"
            onClick={goToTemplate}
          >
            &larr; Back to Template Settings
          </div>
          <div className={classNames(styles.heading)}>Customize Current Template</div>
          <div
            className={classNames(
              styles.subtitleContainer,
              'is-subtitle with-light-text-color mb-1'
            )}
          >
            Customize your current template for your collection.
          </div>
          <ButtonBlack
            className="has-fit-content-width mb-3"
            loading={loading}
            onClick={onUseTemplate}
          >
            Save template &rarr;
          </ButtonBlack>
        </div>
        {preview ? (
          <div className="is-flex is-align-items-center is-justify-content-flex-end">
            <span className="is-small-subtitle">Preview Mode</span>
            <ButtonBoxBorder
              className={classNames('m-3 mr-5 with-accent-text-color with-accent-border-color')}
              onClick={async () => {
                await getCurrentValueFromIframe();
                setpreview((prev) => !prev);
              }}
            >
              <Edit2 size={18} style={{ marginRight: 5 }} />
              Back to Editing
            </ButtonBoxBorder>
          </div>
        ) : (
          <ButtonBoxBorder
            className={classNames('m-3 mr-5 with-lighter-text-color')}
            onClick={async () => {
              await getCurrentValueFromIframe();
              setpreview((prev) => !prev);
            }}
          >
            <Eye size={18} style={{ marginRight: 5 }} />
            Preview
          </ButtonBoxBorder>
        )}
      </div>

      <div className={styles.hiddenContainer}>
        <div
          className={classNames({
            [styles.hidden]: preview,
          })}
        >
          <Customize id={CUSTOMIZE_TEMPLATE_UPDATED_IFRAME} />
        </div>
        <div
          className={classNames({
            [styles.hidden]: !preview,
          })}
        >
          <Preview elementTemplate={template} editorID="customize-template" />
        </div>
      </div>
    </div>
  );
}

export default CustomizeTemplate;
